import { Map } from 'immutable'
import * as React from 'react'
import { ApiResult } from '../../../Shared/api/apiTypes'
import { Announcement } from '../../../Shared/components/Announcement'
import { LocalizationProps } from '../../../Shared/components/Jumbo/BranchLocalization/BranchLocalizationLayout'
import { features } from '../../../Shared/features'
import { scrollToTop } from '../../../Shared/utils/browser'
import { getPage, Region } from '../../custom_api/api'
import { PeriodSlug } from '../components/region_and_date_filter'
import { ActivityClustersPage, ActivityClustersPageProps } from '../pages/activity_clusters_page'
import { ActivityDetailPage, ActivityDetailPageProps } from '../pages/activity_detail_page'
import { BlogOverviewProps } from '../pages/blogOverview/blogOverviewState'
import { BlogOverview } from '../pages/blogOverview/blogOverviewWidget'
import { BlogPost } from '../pages/blogPost/blogPostLayout'
import { BlogPostProps } from '../pages/blogPost/blogPostState'
import { CollectionPage, CollectionPageProps } from '../pages/collection_page'
import { GeneralContentPage, GeneralContentPageProps } from '../pages/general_content_page'
import { HomePage, HomePageProps } from '../pages/home_page'
import { LocationOverviewPage, LocationOverviewPageProps } from '../pages/Location/LocationOverviewPage/location_overview_page'
import { LocationPage } from '../pages/Location/LocationPage/LocationPageLayout'
import { LocationPageProps } from '../pages/Location/LocationPage/LocationPageState'
import { LocationDetailPage, LocationDetailPageDrupalProps } from '../pages/location_detail_page'
import { NewsItem } from '../pages/newsItem/newsItemLayout'
import { NewsItemProps } from '../pages/newsItem/newsItemState'
import { NewsletterSignupPage, NewsletterSignupPageProps } from '../pages/newsletter_signup_page'
import { NewsOverviewProps } from '../pages/newsOverview/newsOverviewState'
import { NewsOverview } from '../pages/newsOverview/newsOverviewWidget'
import { ReportOverviewPage, ReportOverviewPageProps } from '../pages/report_overview_page'
import { drupalHomepagePath } from './application'
import { CookieUpdater } from './cookiebar'
import { Error } from './error'
import { Loader } from './loader'
import { ready } from '../../../Shared/api/apiUtils'

export type Page =
  | ActivityDetailPageProps
  | ActivityClustersPageProps
  | BlogOverviewProps
  | BlogPostProps
  | CollectionPageProps
  | NewsItemProps
  | NewsOverviewProps
  | NewsletterSignupPageProps
  | GeneralContentPageProps
  | LocationPageProps
  | LocationDetailPageDrupalProps
  | LocationOverviewPageProps
  | ReportOverviewPageProps
  | HomePageProps

export interface BasePageProps { }
interface PageRendererProps {
  readonly route: string
  readonly params: string
  readonly regions: Map<string, Region>
  readonly selectedPeriod: PeriodSlug
  readonly selectedRegion: string
  readonly selectedCluster: 'none' | string
  readonly selectPeriod: (e: React.ChangeEvent<HTMLSelectElement>) => void
  readonly selectRegion: (e: React.ChangeEvent<HTMLSelectElement>) => void
  readonly resetPeriod: () => void
  readonly toggleArchive: (cb?: () => void) => void
  readonly showArchive: boolean
  readonly cookieUpdater: CookieUpdater
  readonly localizationProps?: LocalizationProps
}

interface PageRendererState {
  readonly page: ApiResult<Page>
  readonly homepage: ApiResult<HomePageProps>
}

const pageRendererStateZero: PageRendererState = {
  page: 'loading',
  homepage: 'loading'
}

export class PageRenderer<A> extends React.Component<PageRendererProps, PageRendererState> {
  constructor(props: PageRendererProps) {
    super(props)

    this.state = pageRendererStateZero
  }

  componentDidUpdate(prevProps: PageRendererProps, prevState: PageRendererState) {
    if (prevProps.route != this.props.route) {
      this.loadRoute()
    }
  }

  componentDidMount() {
    this.initialLoad()
  }

  initialLoad() {
    if (this.props.route === drupalHomepagePath) {
      getPage(drupalHomepagePath, this.props.params, this.props.cookieUpdater).then(x => {
        this.setState(s => ({ ...s, page: x, homepage: x as HomePageProps }), scrollToTop)
      })
    } else {
      getPage(this.props.route, this.props.params, this.props.cookieUpdater).then(x => {
        this.setState(s => ({ ...s, page: x }), scrollToTop)
      })
      getPage(drupalHomepagePath, this.props.params, this.props.cookieUpdater).then(x => {
        this.setState(s => ({ ...s, homepage: x as HomePageProps }))
      })
    }

  }

  loadRoute() {
    this.setState(
      s => ({ ...s, page: 'loading' }),
      () => {
        getPage(this.props.route, this.props.params, this.props.cookieUpdater).then(x => this.setState(s => ({ ...s, page: x })))
        scrollToTop()
      }
    )
  }

  render() {
    const p = this.props
    const s = this.state
    const pg = s.page

    const page: JSX.Element =
      pg == 'loading' ? ( // TH: Added to styleguide!
        <Loader />
      ) : pg == 'error' || pg == null ? ( // TH: Added to styleguide!
        <Error />
      ) : pg.kind == 'activityDetail' ? ( // TH: Added to styleguide!
        <ActivityDetailPage
          {...pg}
          regions={p.regions}
          selectedPeriod={p.selectedPeriod}
          selectedRegion={p.selectedRegion}
          selectPeriod={p.selectPeriod}
          selectRegion={p.selectRegion}
        />
      ) : pg.kind == 'activityClusters' ? ( // TH: Added to styleguide!
        <ActivityClustersPage
          {...pg}
          regions={p.regions}
          selectedPeriod={p.selectedPeriod}
          selectedRegion={p.selectedRegion}
          selectedCluster={p.selectedCluster}
          selectPeriod={p.selectPeriod}
          selectRegion={p.selectRegion}
          toggleArchive={p.toggleArchive}
          showArchive={p.showArchive}
        />
      ) : pg.kind == 'blogPost' ? (
        <BlogPost {...pg} />
      ) : pg.kind == 'blogOverview' ? (
        <BlogOverview {...pg} />
      ) : pg.kind == 'newsItem' ? (
        <NewsItem {...pg} />
      ) : pg.kind == 'newsOverview' ? (
        <NewsOverview {...pg} />
      ) : pg.kind == 'generalContent' ? ( // TH: Added to styleguide!
        <GeneralContentPage {...pg} />
      ) : pg.kind == 'collection' ? ( // TH: Added to styleguide!
        <CollectionPage {...pg} />
      ) : pg.kind == 'newsletterSignup' ? ( // TH: Added to styleguide!
        <NewsletterSignupPage {...pg} />
      ) : pg.kind == 'locationDetail' ? ( // TH: Added to styleguide!
        <LocationDetailPage
          {...pg}
          resetPeriod={p.resetPeriod}
          region={p.regions.find(r => r.locations.includes(pg.locationId))}
          localizationProps={p.localizationProps}
          homePageData={s.homepage}
        />
      ) : pg.kind == 'locationOverviewContent' ? (
        <LocationOverviewPage {...pg} />
      ) : pg.kind == 'locationContent' ? (
        <LocationPage {...pg} />
      ) : pg.kind == 'reportOverview' ? ( // TH: Added to styleguide!
        <ReportOverviewPage {...pg} />
      ) : pg.kind == 'home' ? ( // TH: Added to styleguide!
        <HomePage {...pg} localizationProps={p.localizationProps} />
      ) : null

    const showAnnouncement = ready(pg) && features().showAnnouncementOnPage(pg)

    return <>
      {showAnnouncement && <Announcement key="general-announcement" homepage={this.state.homepage} />}
      {page}
    </>
  }
}
